import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import api from '../../config/api';
import { theme } from '../../styles/theme';
import { toast } from 'react-toastify';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;

const Controls = styled.div`
  display: flex;
  gap: ${theme.spacing.md};
  align-items: center;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px;
  border-bottom: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
`;

const Td = styled.td`
  padding: 8px;
  border-bottom: 1px solid ${theme.colors.border.secondary};
  color: ${theme.colors.text.primary};
`;

const Input = styled.input`
  padding: 6px 8px;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: 6px;
  width: 100%;
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  ::placeholder { color: ${theme.colors.text.secondary}; }
  &:focus { outline: none; border-color: ${theme.colors.primary.main}; box-shadow: 0 0 0 2px ${theme.colors.primary.main}22; }
`;

const Select = styled.select`
  padding: 6px 8px;
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: 6px;
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  &:focus { outline: none; border-color: ${theme.colors.primary.main}; box-shadow: 0 0 0 2px ${theme.colors.primary.main}22; }
`;

const Button = styled.button`
  padding: 8px 12px;
  border: none;
  border-radius: 6px;
  background: ${props => props.primary ? theme.colors.primary.main : theme.colors.background.paper};
  color: ${props => props.primary ? 'white' : theme.colors.text.primary};
  border: ${props => props.primary ? 'none' : `1px solid ${theme.colors.border.primary}`};
  cursor: pointer;
`;

const PGNDemoAdmin = () => {
  const [items, setItems] = useState([]);
  const [savingByPgn, setSavingByPgn] = useState({});
  const [statusByPgn, setStatusByPgn] = useState({});
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sortBy, setSortBy] = useState({ key: 'category', dir: 'asc' });
  const categories = useMemo(() => {
    const set = new Set(items.map(i => i.category).filter(Boolean));
    return [''].concat(Array.from(set));
  }, [items]);

  const sortItems = (arr, sorter = sortBy) => {
    const { key, dir } = sorter || {};
    const dirMul = dir === 'desc' ? -1 : 1;
    const getVal = (r) => {
      const v = r[key];
      if (v === null || v === undefined) return '';
      return v;
    };
    const numericKeys = new Set(['pgn', 'min_value', 'max_value', 'variance', 'step']);
    const boolKeys = new Set(['is_active']);
    return arr.sort((a, b) => {
      let va = getVal(a);
      let vb = getVal(b);
      if (numericKeys.has(key)) {
        va = Number(va) || 0;
        vb = Number(vb) || 0;
        return (va - vb) * dirMul;
      }
      if (boolKeys.has(key)) {
        // true > false in asc
        return ((va ? 1 : 0) - (vb ? 1 : 0)) * dirMul;
      }
      // string compare
      va = String(va).toLowerCase();
      vb = String(vb).toLowerCase();
      if (va < vb) return -1 * dirMul;
      if (va > vb) return 1 * dirMul;
      // secondary sort by name then pgn for stability
      const sa = String(a.name || '').toLowerCase();
      const sb = String(b.name || '').toLowerCase();
      if (sa < sb) return -1 * dirMul;
      if (sa > sb) return 1 * dirMul;
      return ((Number(a.pgn) || 0) - (Number(b.pgn) || 0)) * dirMul;
    });
  };

  const load = async () => {
    const res = await api.get('/api/admin/pgns', { params: { search, category, active: activeOnly } });
    const list = (res.data.pgns || []).map(r => ({ ...r, original_pgn: r.pgn }));
    setItems(sortItems(list));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const rateFor = (v) => {
    const val = Number(v);
    if (val <= 0.015) return 'low';
    if (val >= 0.06) return 'high';
    return 'normal';
  };

  const varianceForRate = (rate) => {
    if (rate === 'low') return 0.01;
    if (rate === 'high') return 0.08;
    return 0.03; // normal
  };

  const computeStep = (row) => {
    const unit = String(row.unit || '').toLowerCase();
    if (unit === 'kn') return 0.5;
    if (unit === 'mph') return 1;
    if (unit === '°' || unit === 'deg' || unit === 'deg°') return 1;
    if (unit === 'rpm') return 50;
    if (unit === 'v') return 0.05;
    if (unit === 'hpa') return 1;
    if (unit === '%') return 1;
    if (unit === 'm') return 0.5;
    if (unit === '°c' || unit === 'c') return 0.5;
    const min = Number(row.min_value), max = Number(row.max_value);
    if (!isNaN(min) && !isNaN(max) && max > min) return (max - min) / 40;
    return 1;
  };

  const saveRow = async (row) => {
    try {
      setSavingByPgn(prev => ({ ...prev, [row.pgn]: true }));
      setStatusByPgn(prev => ({ ...prev, [row.pgn]: undefined }));
      const toNum = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
      const payload = {
        ...row,
        min_value: toNum(row.min_value),
        max_value: toNum(row.max_value),
        step: computeStep(row),
        variance: toNum(row.variance)
      };
      if (!row.pgn || Number(row.pgn) < 0 || row.__isNew) {
        await api.post(`/api/admin/pgns`, payload);
        setStatusByPgn(prev => ({ ...prev, [row.pgn]: 'saved' }));
        setTimeout(() => setStatusByPgn(prev => ({ ...prev, [row.pgn]: undefined })), 2000);
        toast.success(`Created demo sample`);
      } else {
        await api.put(`/api/admin/pgns/${row.original_pgn || row.pgn}`, payload);
        setStatusByPgn(prev => ({ ...prev, [row.pgn]: 'saved' }));
        setTimeout(() => setStatusByPgn(prev => ({ ...prev, [row.pgn]: undefined })), 2000);
        toast.success(`Updated PGN ${row.pgn}`);
      }
      load();
    } catch (e) {
      setStatusByPgn(prev => ({ ...prev, [row.pgn]: 'error' }));
      if (e?.response?.status === 409) {
        toast.error('PGN already exists. Choose a different PGN number.');
      } else {
        toast.error('Save failed');
      }
    } finally {
      setSavingByPgn(prev => ({ ...prev, [row.pgn]: false }));
    }
  };

  const unitOptionsFor = (row) => {
    const path = String(row.signal_k_path || '').toLowerCase();
    const cat = String(row.category || '').toLowerCase();
    const name = String(row.name || '').toLowerCase();
    const opts = [];
    const push = (arr) => arr.forEach(u => { if (!opts.includes(u)) opts.push(u); });
    if (path.includes('speed') || name.includes('speed') || cat.includes('navigation')) {
      push(['kn', 'mph', 'm/s', 'km/h']);
    }
    if (path.includes('heading') || path.includes('rudder') || path.includes('attitude')) {
      push(['°', 'rad']);
    }
    if (path.includes('temperature') || name.includes('temperature')) {
      push(['°C', '°F', 'K']);
    }
    if (path.includes('pressure') || name.includes('pressure')) {
      push(['hPa', 'kPa', 'Pa', 'psi']);
    }
    if (path.includes('depth')) {
      push(['m', 'ft', 'fathoms']);
    }
    if (path.includes('currentlevel') || path.includes('level') || name.includes('level')) {
      push(['%', 'ratio']);
    }
    if (path.includes('voltage')) {
      push(['V']);
    }
    if (path.includes('revolutions') || name.includes('rpm')) {
      push(['RPM']);
    }
    if (opts.length === 0) {
      push(['%', '°', 'm', 'kn', 'V', 'RPM', 'hPa']);
    }
    // Ensure current unit is selectable
    const current = row.unit || '';
    if (current && !opts.includes(current)) opts.unshift(current);
    return opts;
  };

  const generateDashboard = async () => {
    try {
      const active = items.filter(i => i.is_active !== false).map(i => i.pgn);
      await api.post('/api/admin/pgns/generate-dashboard', { selected: active, name: 'PGN Demo Dashboard' });
      toast.success('Demo dashboard created');
    } catch (e) {
      toast.error('Failed to generate dashboard');
    }
  };

  const addNewRow = () => {
    if (creating) return;
    setCreating(true);
    const newRow = {
      pgn: '',
      original_pgn: '',
      __isNew: true,
      name: '',
      category: 'custom',
      signal_k_path: '',
      unit: '',
      min_value: 0,
      max_value: 100,
      variance: 0.03,
      is_active: true
    };
    setItems(prev => [newRow, ...prev]);
    setTimeout(() => setCreating(false), 200);
  };

  const onHeaderClick = (key) => {
    setSortBy(prev => {
      const next = prev.key === key ? { key, dir: (prev.dir === 'asc' ? 'desc' : 'asc') } : { key, dir: 'asc' };
      // apply to current items
      setItems(curr => sortItems([...curr], next));
      return next;
    });
  };

  const sortIndicator = (key) => {
    if (sortBy.key !== key) return '';
    return sortBy.dir === 'asc' ? ' ▲' : ' ▼';
  };

  return (
    <Container>
      <Controls>
        <Input placeholder="Search by name or PGN" value={search} onChange={e => setSearch(e.target.value)} />
        <Select value={category} onChange={e => setCategory(e.target.value)}>
          {categories.map(c => <option key={c} value={c}>{c || 'All Categories'}</option>)}
        </Select>
        <label style={{display:'flex',alignItems:'center',gap:8}}>
          <input type="checkbox" checked={activeOnly} onChange={e => setActiveOnly(e.target.checked)} /> Active only
        </label>
        <Button onClick={load}>Filter</Button>
        <div style={{flex:1}} />
        <Button onClick={addNewRow}>Add Sample</Button>
        <Button primary onClick={generateDashboard}>Generate Demo Dashboard</Button>
      </Controls>

      <Table>
        <thead>
          <tr>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('pgn')}>PGN{sortIndicator('pgn')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('name')}>Name{sortIndicator('name')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('category')}>Category{sortIndicator('category')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('signal_k_path')}>SignalK Path{sortIndicator('signal_k_path')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('unit')}>Unit{sortIndicator('unit')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('min_value')}>Min{sortIndicator('min_value')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('max_value')}>Max{sortIndicator('max_value')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('variance')}>Change Rate{sortIndicator('variance')}</Th>
            <Th style={{cursor:'pointer'}} onClick={() => onHeaderClick('is_active')}>Active{sortIndicator('is_active')}</Th>
            <Th></Th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, idx) => (
            <tr key={`${row.pgn ?? 'new'}-${idx}`}>
              <Td>
                <Input
                  type="number"
                  value={(row.pgn === 0 || row.pgn) ? row.pgn : ''}
                  onChange={e => {
                    const val = e.target.value;
                    setItems(prev => prev.map(r => r === row ? { ...r, pgn: val === '' ? '' : Number(val), original_pgn: r.original_pgn || r.pgn } : r));
                  }}
                  placeholder={row.__isNew ? 'auto' : 'PGN'}
                />
              </Td>
              <Td><Input value={row.name || ''} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,name:e.target.value}:r))} /></Td>
              <Td><Input value={row.category || ''} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,category:e.target.value}:r))} /></Td>
              <Td><Input value={row.signal_k_path || ''} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,signal_k_path:e.target.value}:r))} /></Td>
              <Td>
                <Select value={row.unit || ''} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,unit:e.target.value}:r))}>
                  {unitOptionsFor(row).map(u => (
                    <option key={u} value={u}>{u || '—'}</option>
                  ))}
                </Select>
              </Td>
              <Td><Input type="number" value={(row.min_value === 0 || row.min_value) ? row.min_value : ''} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,min_value:e.target.value}:r))} /></Td>
              <Td><Input type="number" value={(row.max_value === 0 || row.max_value) ? row.max_value : ''} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,max_value:e.target.value}:r))} /></Td>
              <Td>
                <Select value={rateFor(row.variance)} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,variance:varianceForRate(e.target.value)}:r))}>
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                </Select>
              </Td>
              <Td><input type="checkbox" checked={row.is_active !== false} onChange={e => setItems(prev => prev.map(r => r.pgn===row.pgn?{...r,is_active:e.target.checked}:r))} /></Td>
              <Td>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Button onClick={() => saveRow(items.find(r => r.pgn===row.pgn))} disabled={!!savingByPgn[row.pgn]} primary>
                    {savingByPgn[row.pgn] ? 'Saving…' : (row.__isNew ? 'Create' : 'Save')}
                  </Button>
                  {statusByPgn[row.pgn] === 'saved' && (
                    <span style={{ color: '#059669', fontSize: 12, fontWeight: 600 }}>Saved</span>
                  )}
                  {statusByPgn[row.pgn] === 'error' && (
                    <span style={{ color: '#dc2626', fontSize: 12, fontWeight: 600 }}>Error</span>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
};

export default PGNDemoAdmin;
